import {
  Button,
  Card,
  Drawer,
  Grid,
  Input,
  Tooltip,
} from '@design-systems/canvas-ui'
import Icon, {
  ChatIcon,
  TimeHistoryIcon,
  InfoIcon,
} from '@design-systems/react-icons'
import { useEffect, useMemo, useState } from 'react'
import {
  COMMENTS,
  CONTROL_LABEL_READONLY_BANNER,
  PARAM_ACTIVE_COMMENTS_TAB,
  PARAM_ACTIVE_TAB,
  TARGET,
  VIEW_SUPPLIER,
} from '../../common/commonConstants'
import { getObjectDataNode } from '../../common/helper'
import { RFIAuditTrail } from '../RfiCommons'
import { RFIComments } from '../RfiCommons/popup'
import {
  getRFIApprovalComments,
  getRFIAuditTrails,
} from '../../services/lcmService'
import DynamicSectionBuilder from './DynamicSectionBuilder'

const HeaderBuilder = (props) => {
  const [isCommentsDrawerOpen, setIsCommentsDrawerOpen] = useState(false)
  const [isAuditTrailDrawerOpen, setIsAuditTrailDrawerOpen] = useState(false)
  const [commentsCount, setCommentsCount] = useState(0)

  const urlParameters = useMemo(
    () => new URLSearchParams(window.location.search),
    [],
  )
  const commentsTab = urlParameters.get(PARAM_ACTIVE_COMMENTS_TAB)

  useEffect(() => {
    if (urlParameters.get(PARAM_ACTIVE_TAB)?.toLowerCase() === COMMENTS) {
      setIsCommentsDrawerOpen(true)
    }
  }, [urlParameters])

  useEffect(() => {
    if (!props.isReadonly || !props.supplierId || !props.setAuditData) return

    const loadAuditData = async () => {
      try {
        const auditData = await getRFIAuditTrails(
          props.rfiType,
          props.supplierId,
          props.locationId,
          props.manufacturingTypeId,
        )
        props.setAuditData(Array.isArray(auditData) ? auditData : [])
      } catch {
        props.setAuditData([])
      }
    }

    loadAuditData()
  }, [
    props.isReadonly,
    props.rfiType,
    props.supplierId,
    props.locationId,
    props.manufacturingTypeId,
    props.setAuditData,
  ])

  useEffect(() => {
    if (!props.supplierId) return

    const loadCommentsCount = async () => {
      try {
        const response = await getRFIApprovalComments(
          props.rfiType,
          props.supplierId,
          props.locationId,
          props.isReadonly ? TARGET : VIEW_SUPPLIER,
          props.manufacturingTypeId,
        )
        const comments = response?.data || {}
        setCommentsCount(
          Array.isArray(comments)
            ? comments.length
            : Object.values(comments).reduce(
                (count, commentList) =>
                  count + (Array.isArray(commentList) ? commentList.length : 0),
                0,
              ),
        )
      } catch {
        setCommentsCount(0)
      }
    }

    loadCommentsCount()
  }, [
    props.rfiType,
    props.supplierId,
    props.locationId,
    props.isReadonly,
    props.manufacturingTypeId,
  ])

  const buildHeaderSections = () => {
    return props.headerData?.fixedSectionsTemplate?.map(
      (eachHeaderSection, sectionIndex) => {
        const sectionRfiObject = getObjectDataNode(
          props.rfiObject,
          eachHeaderSection.uid,
        )
        const headerSectionTemplate = { ...eachHeaderSection }

        if (!props.isNonCompliant && !props.isUFLPA) {
          headerSectionTemplate.controls = eachHeaderSection.controls.filter(
            (control) =>
              control.type?.toLowerCase() !== CONTROL_LABEL_READONLY_BANNER,
          )
        }

        return (
          <DynamicSectionBuilder
            key={eachHeaderSection.uid || sectionIndex}
            isHeaderSection
            sectionTemplate={headerSectionTemplate}
            sectionData={sectionRfiObject}
          />
        )
      },
    )
  }

  return (
    <Card
      id="card-header-main"
      className="card-section hc8-pa-3x border-bottom-grey-thin"
    >
      <Grid.Container align="center" spacing="none">
        <Grid.Item size={{ xs: 9 }}>
          <div className="row-centre">
            <Input.Label className="hc8-fs-350 hc8-mb-none font-weight-500">
              {props.headerData?.rfiHeaderText}
            </Input.Label>

            {props.headerData?.infoIconText && (
              <Tooltip
                className="hc8-ml-2x"
                content={props.headerData.infoIconText}
                location="right"
              >
                <span
                  className="row-centre"
                  tabIndex={0}
                  aria-label="Header information"
                >
                  <Icon className="row-centre" svg={InfoIcon} />
                </span>
              </Tooltip>
            )}
          </div>
        </Grid.Item>

        <Grid.Item size={{ xs: 3 }}>
          <div
            className="align-centre-right"
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: '8px',
              whiteSpace: 'nowrap',
            }}
          >
            {props.isReadonly && (
              <Tooltip
                content="View full history of changes to this RFI form"
                location="bottom"
              >
                <Button
                  onClick={() => setIsAuditTrailDrawerOpen(true)}
                  variant={isAuditTrailDrawerOpen ? 'primary' : 'ghost'}
                  aria-label="Click to open Audit Trail side window"
                  style={{
                    border: '1px solid #4a73d9',
                  }}
                >
                  <Icon className="row-centre" svg={TimeHistoryIcon} />
                  <span className="hc8-ml-1x">Audit Trail</span>
                </Button>
              </Tooltip>
            )}

            <Tooltip
              content="Add comments or @mention Target team members or Supplier"
              location="bottom"
            >
              <Button
                onClick={() => setIsCommentsDrawerOpen(true)}
                variant={isCommentsDrawerOpen ? 'primary' : 'ghost'}
                aria-label="Click to open chat side window"
                style={{
                  border: '1px solid #4a73d9',
                }}
              >
                <Icon className="row-centre" svg={ChatIcon} />
                <span className="hc8-ml-1x">Comments ({commentsCount})</span>
              </Button>
            </Tooltip>
          </div>
        </Grid.Item>
      </Grid.Container>

      <Drawer
        isOpen={isCommentsDrawerOpen}
        onRequestClose={() => setIsCommentsDrawerOpen(false)}
        headingText={<span>Comments ({commentsCount})</span>}
        location="right"
        xs={3}
      >
        <div className="header-builder__drawer-content">
          <RFIComments
            rfiType={props.rfiType}
            supplierId={props.supplierId}
            supplierName={props.supplierName}
            setRFICommentsLen={(count) => setCommentsCount(count)}
            locationId={props.locationId}
            locationName={props.locationName}
            manufacturingType={props.manufacturingType}
            manufacturingTypeId={props.manufacturingTypeId}
            view={props.isReadonly ? TARGET : VIEW_SUPPLIER}
            focusCommentTab={commentsTab}
          />
        </div>
      </Drawer>

      <Drawer
        isOpen={isAuditTrailDrawerOpen}
        onRequestClose={() => setIsAuditTrailDrawerOpen(false)}
        headingText={<span>Audit Trail</span>}
        location="right"
        xs={3}
      >
        <div className="header-builder__drawer-content">
          <RFIAuditTrail
            isReadonly={props.isReadonly}
            rfiType={props.rfiType}
            manufacturingTypeId={props.manufacturingTypeId}
            supplierId={props.supplierId}
            vettingStatus={props.vettingStatus}
            locationId={props.locationId}
            refreshRFIScreen={props.refreshRFIScreen}
            setAuditData={props.setAuditData}
          />
        </div>
      </Drawer>

      {buildHeaderSections()}
    </Card>
  )
}

export default HeaderBuilder
